import React, { useEffect, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import RadioPayment from "../../../components/inputs/radioPayment";
import { CheckLg, XLg } from "react-bootstrap-icons";
import { usePaymentPlansQuery } from "../../../graphql/payment.hooks";
import { isPaymentError, selectedCurrency } from "../../../graphql/state";
import { useReactiveVar } from "@apollo/client";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { LARGE_TRANSLATIONS } from "../../../constants/languages";
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}
export default function PremiumPlans() {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const currentCurrency = useReactiveVar(selectedCurrency);
  const [isAnnually, setIsAnnually] = useState(true);

  const { data, loading, error } = usePaymentPlansQuery({
    variables: { locale: "en", currency: currentCurrency },
  });

  useEffect(() => {
    if (!error) return;
    isPaymentError(true);
  }, [error]);

  const annualyPlan = data?.paymentPlans.plans.find(
    (item) => item.planName === "year"
  );

  const monthlyPlan = data?.paymentPlans.plans.find(
    (item) => item.planName === "month"
  );

  const currentPlan = data?.paymentPlans?.plans.find(
    (item) => item.planName === data?.paymentPlans?.type
  );

  const isDisabledSubscription = !data?.paymentPlans?.activePremium
    ? false
    : (currentPlan?.planName === "month" && !isAnnually) ||
      (currentPlan?.planName === "year" && isAnnually);
  const isLargeTranslation = LARGE_TRANSLATIONS.includes(lng.slice(1));
  const trySubscribeToPremium = () => {
    // 1. Google Analytics
    if (window.gtag) {
      if (isAnnually) {
        window.gtag("event", "subscription_for_year_admin");
      } else {
        window.gtag("event", "monthly_subscription_admin");
      }
    }

    // 2. Facebook Pixel
    window.fbq("trackCustom", "subscription");

    // 3. LinkedIn Insight Tag
    window.lintrk?.("track", { conversion_id: 22347289 });

    // 4. Задержка, чтобы события успели отправиться
    setTimeout(() => {
      const plan = isAnnually ? annualyPlan : monthlyPlan;
      const linkToPayment = plan?.checkoutUrlTrial || plan?.checkoutUrl;

      window.location.href = linkToPayment;
    }, 200);
  };

  return (
    <div className="bg-white rounded p-4">
      <div className="mb-4 fw-bold fs-4">{t("payment.Premium Plans")}</div>
      <div className="p-4 rounded border">
        <Row className="mb-4 gy-3">
          <Col md={6} lg={12} xxl={6}>
            <RadioPayment
              id="radio-payment"
              checked={isAnnually}
              isLargeTranslation={isLargeTranslation}
              onChange={() => setIsAnnually(true)}
            >
              <div
                className={`d-flex ${isLargeTranslation && "flex-column"} justify-content-between align-items-center w-100 h-100`}
              >
                <div
                  className={`col-12 d-flex ${isLargeTranslation && "flex-wrap"} justify-content-between`}
                >
                  <div className="d-lg-flex flex-wrap align-items-center gap-2">
                    <div className="fw-bold fs-5">{t("payment.Annually")}</div>
                    {!isLargeTranslation && (
                      <div className="rounded px-2 px-xxl-1 bg-warning-light fs-7 text-secondary fw-bold py-1">
                        {t("projects.Save")}{" "}
                        {
                          monthlyPlan?.currencyAmountByCurrency[0]
                            .currencySymbol
                        }
                        {monthlyPlan?.currencyAmountByCurrency ? (
                          (
                            monthlyPlan?.currencyAmountByCurrency[0].amount *
                              12 -
                            annualyPlan?.currencyAmountByCurrency[0].amount
                          ).toFixed(2)
                        ) : (
                          <Spinner
                            animation="border"
                            role="status"
                            size="sm"
                            className="ms-2"
                          />
                        )}
                      </div>
                    )}
                  </div>
                  {loading ? (
                    <Spinner
                      animation="border"
                      role="status"
                      size="sm"
                      className="ms-2"
                    />
                  ) : (
                    <div className="text-nowrap">
                      <span className="fw-bold fs-4">
                        {
                          annualyPlan?.currencyAmountByCurrency[0]
                            .currencySymbol
                        }
                        {annualyPlan?.currencyAmountByCurrency[0].amount}
                      </span>{" "}
                      / {t("payment.year")}
                    </div>
                  )}
                </div>
                {isLargeTranslation && (
                  <div className="col-12 d-flex">
                    <div className="text-center rounded px-2 px-xxl-1 bg-warning-light fs-7 text-secondary fw-bold py-1">
                      {t("projects.Save")}{" "}
                      {monthlyPlan?.currencyAmountByCurrency[0].currencySymbol}
                      {monthlyPlan?.currencyAmountByCurrency ? (
                        (
                          monthlyPlan?.currencyAmountByCurrency[0].amount * 12 -
                          annualyPlan?.currencyAmountByCurrency[0].amount
                        ).toFixed(2)
                      ) : (
                        <Spinner
                          animation="border"
                          role="status"
                          size="sm"
                          className="ms-2"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </RadioPayment>
          </Col>
          <Col md={6} lg={12} xxl={6}>
            <RadioPayment
              id="radio-payment"
              checked={!isAnnually}
              isLargeTranslation={isLargeTranslation}
              onChange={() => setIsAnnually(false)}
            >
              <div
                className={`d-flex justify-content-between  ${!isLargeTranslation && "align-items-center"} w-100 h-100`}
              >
                <div className="fw-bold fs-5">{t("payment.Monthly")}</div>
                {loading ? (
                  <Spinner
                    animation="border"
                    role="status"
                    size="sm"
                    className="ms-2"
                  />
                ) : (
                  <div>
                    <span className="fw-bold fs-4">
                      {monthlyPlan?.currencyAmountByCurrency[0].currencySymbol}
                      {monthlyPlan?.currencyAmountByCurrency[0].amount}
                    </span>{" "}
                    / {t("payment.month")}
                  </div>
                )}
              </div>
            </RadioPayment>
          </Col>
        </Row>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg">
          <div>{t("payment.Published Pages")}</div>
          <div className="fw-bold fs-7 text-end">{t("payment.Unlimited")}</div>
        </div>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg">
          <div>{t("payment.Projects")}</div>
          <div className="fw-bold fs-7 text-end">{t("payment.Unlimited")}</div>
        </div>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg">
          <div>{t("payment.Subpages")}</div>
          <div className="fw-bold fs-7 text-end">{t("payment.Unlimited")}</div>
        </div>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg">
          <div>{t("payment.Blocks per Page")}</div>
          <div className="fw-bold fs-7 text-end">{t("payment.Unlimited")}</div>
        </div>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg">
          <div>{t("payment.Templates")}</div>
          <div className="fw-bold fs-7">500+</div>
        </div>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg">
          <div>{t("payment.SEO Settings")}</div>
          <div className="fw-bold">
            <CheckLg className="text-success" />
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg">
          <div>{t("payment.Custom Domain")}</div>
          <div className="fw-bold">
            <CheckLg className="text-success" />
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg fw-bold">
          <div>{t("payment.Advanced Mode")}</div>
          <div className="fw-bold">
            <CheckLg className="text-success" />
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg fw-bold">
          <div>{t("payment.Remove Branding")}</div>
          <CheckLg className="text-success" />
        </div>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg fw-bold">
          <div>{t("payment.Advertising on Pages")}</div>
          <div className="d-flex align-items-center fw-bold fs-7 text-nowrap text-end">
            <XLg className="text-dark-gray bold-icon fs-7 me-2 mb-1" />
            {t("payment.No Ads")}
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center px-0 px-md-3 py-2 lh-lg fw-bold">
          <div>{t("menu.support")}</div>
          <div className="fw-bold text-dark-gray fs-7 text-end">
            {t("payment.Priority Support")}
          </div>
        </div>
        <Button
          disabled={isDisabledSubscription}
          variant="primary text-white mt-3"
          className="w-100"
          onClick={trySubscribeToPremium}
        >
          {data?.paymentPlans?.trialAvailable
            ? t("payment.Try FREE for 7 Days")
            : t("modal.Upgrade Now")}
        </Button>
      </div>
    </div>
  );
}
